import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  IconButton, 
  Typography, 
  AppBar, 
  Toolbar, 
  Menu, 
  MenuItem,
  Chip,
  Avatar,
  Badge,
  Divider,
  ListItemIcon,
  ListItemText,
  InputBase,
  alpha,
  Tooltip,
  LinearProgress,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  ExitToApp, 
  AccountCircle,
  Notifications,
  Settings,
  AdminPanelSettings,
  Lock,
  Dashboard,
  VerifiedUser,
  Search,
  LightMode,
  DarkMode,
  Refresh,
  Help,
  Assignment,
  Hotel,
  RoomService,
  Restaurant,
  Pool,
  KingBed,
  CalendarToday,
  Receipt,
  Close as CloseIcon
} from '@mui/icons-material';
import { useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Importing the logo image
import logo from '../assets/images/logo.png';

// API base URL - adjust according to your setup
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME;

// Styled component for spinning animation
const SpinIcon = styled(Refresh)(({ spinning }) => ({
  animation: spinning ? 'spin 1s linear infinite' : 'none',
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
}));

const HotelTopNav = ({ onLogout, toggleSidebar, toggleTheme, isDarkMode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(max-width:1024px)');
  const navigate = useNavigate();
  
  // State for hotel profile data and statistics
  const [hotelProfile, setHotelProfile] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const searchInputRef = useRef(null);
  
  // Password change dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  // Get hotel code from user context or localStorage - Matching the pattern from HotelRoomManagement
  const getHotelCode = () => {
    // Retrieve hotel data from localStorage (set during login)
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    const hotelCode = storedProfile.hotelcode || storedProfile.hotel_code || storedProfile.hotelCode || '';
    
    console.log('Stored Profile:', storedProfile);
    console.log('Extracted Hotel Code:', hotelCode);
    
    if (!hotelCode) {
      console.warn('No hotel code found in user profile, using fallback');
      return 'HOTEL_001'; // Fallback code
    }
    
    return hotelCode;
  };

  // API service function for statistics
  const api = {
    getStatistics: async () => {
      const hotel_code = getHotelCode();
      const response = await axios.get(`${API_BASE_URL}/hotel/staff/reception/statistics_topbar.php`, {
        params: { hotel_code }
      });
      return response.data;
    }
  };

  // Fetch hotel profile from localStorage on component mount
  useEffect(() => {
    const profile = JSON.parse(localStorage.getItem("userProfile")) || JSON.parse(localStorage.getItem("hotelProfile"));
    setHotelProfile(profile);
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    try {
      setIsRefreshing(true);
      const data = await api.getStatistics();
      if (data.success) {
        setStatistics(data.statistics);
        console.log('Statistics loaded:', data.statistics);
      } else {
        console.error('Failed to load statistics:', data.error);
        // Set fallback statistics
        setStatistics({
          total_guests: 0,
          available_rooms: 0,
          today_checkins: 0,
          total_rooms: 0,
          occupied_rooms: 0,
          avg_price: 0
        });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      // Set fallback statistics on error
      setStatistics({
        total_guests: 0,
        available_rooms: 0,
        today_checkins: 0,
        total_rooms: 0,
        occupied_rooms: 0,
        avg_price: 0
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    await loadStatistics();
  };

  // Password change handlers
  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordMessage({ type: '', text: '' });
    handleMenuClose();
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordMessage({ type: '', text: '' });
    setPasswordLoading(false);
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear messages when user starts typing
    if (passwordMessage.text) {
      setPasswordMessage({ type: '', text: '' });
    }
  };

  const validatePasswordForm = () => {
    if (!passwordFormData.currentPassword || !passwordFormData.newPassword || !passwordFormData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return false;
    }

    if (passwordFormData.newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters long' });
      return false;
    }

    if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return false;
    }

    if (passwordFormData.currentPassword === passwordFormData.newPassword) {
      setPasswordMessage({ type: 'error', text: 'New password must be different from current password' });
      return false;
    }

    return true;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) return;

    setPasswordLoading(true);
    setPasswordMessage({ type: '', text: '' });

    try {
      const authToken = localStorage.getItem('authToken');
      const userProfile = JSON.parse(localStorage.getItem('userProfile'));

      if (!authToken || !userProfile) {
        setPasswordMessage({ type: 'error', text: 'Authentication required. Please log in again.' });
        setPasswordLoading(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/hotel/staff/change-password.php`,
        {
          current_password: passwordFormData.currentPassword,
          new_password: passwordFormData.newPassword,
          confirm_password: passwordFormData.confirmPassword
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setPasswordMessage({ type: 'success', text: response.data.message });
        setPasswordFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Close dialog after success and logout
        setTimeout(() => {
          handleClosePasswordDialog();
          handleLogout();
        }, 2000);
      } else {
        setPasswordMessage({ type: 'error', text: response.data.error });
      }
    } catch (error) {
      console.error('Password change error:', error);
      
      // More detailed error handling
      if (error.response?.status === 401) {
        setPasswordMessage({ type: 'error', text: 'Session expired. Please log in again.' });
        setTimeout(() => {
          handleClosePasswordDialog();
          handleLogout();
        }, 1500);
      } else if (error.response?.data?.error) {
        setPasswordMessage({ type: 'error', text: error.response.data.error });
      } else {
        setPasswordMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const hotelName = hotelProfile ? (hotelProfile.hotelName || hotelProfile.hotel_name || "Hotel Management") : "Hotel Management";
  const userType = hotelProfile ? hotelProfile.user_type : "staff";
  const email = hotelProfile ? hotelProfile.email : "manager@hotel.com";
  const userName = hotelProfile ? hotelProfile.name : "Hotel Manager";
  const isAdmin = userType === 'hoteladmin';

  // Enhanced notifications data with hotel-specific actions
  const notifications = [
    { 
      id: 1, 
      text: `${statistics?.today_checkins || 0} new check-ins pending`, 
      type: 'checkin', 
      urgent: true, 
      time: '5 min ago',
      action: '/hotel/checkins'
    },
    { 
      id: 2, 
      text: 'Room 301 maintenance required', 
      type: 'maintenance', 
      urgent: true, 
      time: '1 hour ago',
      action: '/hotel/maintenance'
    },
    { 
      id: 3, 
      text: 'VIP guest arriving in 2 hours', 
      type: 'vip', 
      urgent: false, 
      time: '2 hours ago',
      action: '/hotel/vip-arrivals'
    },
    { 
      id: 4, 
      text: 'Breakfast buffet setup complete', 
      type: 'restaurant', 
      urgent: false, 
      time: '1 day ago',
      action: '/hotel/restaurant'
    },
    { 
      id: 5, 
      text: 'Pool maintenance scheduled', 
      type: 'facilities', 
      urgent: false, 
      time: '1 day ago',
      action: '/hotel/facilities'
    },
  ];

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleSearchMenuOpen = (event) => {
    setSearchAnchorEl(event.currentTarget);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationAnchorEl(null);
    setSearchAnchorEl(null);
  };

  const handleNavigate = (path) => {
    handleMenuClose();
    navigate(path);
  };

  const handleNotificationClick = (notification) => {
    handleMenuClose();
    if (notification.action) {
      navigate(notification.action);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleQuickAction = (action) => {
    setSearchQuery('');
    handleMenuClose();
    navigate(action.path);
  };

  const urgentNotificationsCount = notifications.filter(n => n.urgent).length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'checkin': return <Assignment fontSize="small" />;
      case 'maintenance': return <Hotel fontSize="small" />;
      case 'vip': return <VerifiedUser fontSize="small" />;
      case 'restaurant': return <Restaurant fontSize="small" />;
      case 'facilities': return <Pool fontSize="small" />;
      default: return <Notifications fontSize="small" />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'checkin': return '#1976d2';
      case 'maintenance': return '#ed6c02';
      case 'vip': return '#9c27b0';
      case 'restaurant': return '#2e7d32';
      case 'facilities': return '#0097a7';
      default: return '#757575';
    }
  };

  const iconButtonStyle = {
    backgroundColor: alpha(theme.palette.background.paper, 0.1),
    '&:hover': { 
      backgroundColor: alpha(theme.palette.background.paper, 0.2),
      transform: 'scale(1.05)'
    },
    transition: 'all 0.2s ease-in-out',
    borderRadius: '12px',
    padding: '8px',
  };

  // Generate user initials for avatar
  const getUserInitials = () => {
    if (!userName) return 'HM';
    return userName
      .split(' ')
      .map(name => name[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fixed logout function
  const handleLogout = () => {
    // Clear all user-related data from localStorage
    localStorage.removeItem('userProfile');
    localStorage.removeItem('hotelProfile');
    localStorage.removeItem('token');
    localStorage.removeItem('userToken');
    localStorage.removeItem('authToken');
    
    // Call the parent logout function if provided
    if (onLogout) {
      onLogout();
    }
    
    // Navigate to hotel staff login page
    navigate('/');
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={2} 
        sx={{ 
          top: 0,
          background: 'linear-gradient(135deg, #0A2647 0%, #144272 50%, #205295 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        {isRefreshing && (
          <LinearProgress 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0,
              height: 3,
              backgroundColor: 'transparent',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#00ff88'
              }
            }} 
          />
        )}
        
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          minHeight: '70px',
          padding: { xs: '0 12px', sm: '0 20px' }
        }}>
          {/* Left Section: Mobile Menu and Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Mobile Sidebar Toggle Icon */}
            {isMobile && (
              <Tooltip title="Toggle Menu">
                <IconButton 
                  color="inherit" 
                  onClick={toggleSidebar}
                  sx={iconButtonStyle}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}

            {/* Logo and Hotel Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  padding: '8px 16px',
                  borderRadius: '16px',
                  backgroundColor: alpha(theme.palette.background.paper, 0.1),
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.15),
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => navigate('#')}
              >
                <img
                  src={logo}
                  alt="Hotel Logo"
                  style={{ 
                    width: 32, 
                    height: 32, 
                    borderRadius: '8px',
                    filter: 'brightness(0) invert(1)'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white', 
                      fontSize: { xs: '0.9rem', sm: '1.1rem' },
                      fontWeight: 'bold',
                      lineHeight: 1.2
                    }}
                  >
                     {HOTEL_NAME}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.65rem',
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Luxury Hospitality Management
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* User Role Badge */}
            <Chip
              icon={isAdmin ? <AdminPanelSettings /> : <Hotel />}
              label={isAdmin ? 'Hotel Manager' : 'Hotel Staff'}
              size="small"
              color={isAdmin ? 'secondary' : 'default'}
              variant="filled"
              sx={{
                color: 'white',
                backgroundColor: isAdmin 
                  ? 'linear-gradient(135deg, #FF6B35 0%, #FF8E53 100%)' 
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontWeight: 'bold',
                fontSize: '0.7rem',
                height: '28px',
                display: { xs: 'none', md: 'flex' },
                borderRadius: '20px',
                '& .MuiChip-icon': {
                  color: 'white !important',
                  fontSize: '16px'
                }
              }}
            />
          </Box>

          {/* Center Section: Search Bar - Desktop only */}
          {!isMobile && (
            <Box sx={{ flex: 1, maxWidth: 500, mx: 3 }}>
              <Tooltip title="Quick search and actions">
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: alpha(theme.palette.background.paper, 0.1),
                    borderRadius: '12px',
                    padding: '4px 12px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.paper, 0.15),
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={handleSearchMenuOpen}
                >
                  <Chip 
                    label="Ctrl+K" 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: '0.6rem',
                      height: '20px'
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          )}

          {/* Right Section: Actions and Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Quick Stats - Desktop only */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 2, mr: 2 }}>
              <Tooltip title="Current Guests">
                <Box sx={{ 
                  textAlign: 'center', 
                  padding: '6px 12px', 
                  borderRadius: '10px', 
                  backgroundColor: alpha(theme.palette.background.paper, 0.1),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.15),
                  },
                  transition: 'all 0.2s ease-in-out',
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                    Guests
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {statistics?.total_guests || 0}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title="Available Rooms">
                <Box sx={{ 
                  textAlign: 'center', 
                  padding: '6px 12px', 
                  borderRadius: '10px', 
                  backgroundColor: alpha(theme.palette.background.paper, 0.1),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.15),
                  },
                  transition: 'all 0.2s ease-in-out',
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                    Available
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {statistics?.available_rooms || 0}
                  </Typography>
                </Box>
              </Tooltip>
              <Tooltip title="Today Check-ins">
                <Box sx={{ 
                  textAlign: 'center', 
                  padding: '6px 12px', 
                  borderRadius: '10px', 
                  backgroundColor: alpha(theme.palette.background.paper, 0.1),
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.15),
                  },
                  transition: 'all 0.2s ease-in-out',
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                    Check-ins
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {statistics?.today_checkins || 0}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {/* Theme Toggle */}
              {toggleTheme && (
                <Tooltip title={isDarkMode ? "Light Mode" : "Dark Mode"}>
                  <IconButton 
                    color="inherit" 
                    onClick={toggleTheme}
                    sx={iconButtonStyle}
                  >
                    {isDarkMode ? <LightMode /> : <DarkMode />}
                  </IconButton>
                </Tooltip>
              )}

              {/* Refresh */}
              <Tooltip title="Refresh Data">
                <IconButton 
                  color="inherit" 
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  sx={iconButtonStyle}
                >
                  <SpinIcon spinning={isRefreshing} />
                </IconButton>
              </Tooltip>

              {/* Help */}
              <Tooltip title="Help & Support">
                <IconButton 
                  color="inherit" 
                  onClick={() => window.open('https://e-neurons.com.ng/', '_blank', 'noopener,noreferrer')}
                  sx={iconButtonStyle}
                >
                  <Help />
                </IconButton>
              </Tooltip>

              {/* Mobile Search */}
              {isMobile && (
                <Tooltip title="Search">
                  <IconButton 
                    color="inherit" 
                    onClick={handleSearchMenuOpen}
                    sx={iconButtonStyle}
                  >
                    <Search />
                  </IconButton>
                </Tooltip>
              )}

                       </Box>

            {/* User Profile Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              {/* User Info - Desktop only */}
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' }, 
                flexDirection: 'column', 
                alignItems: 'flex-end', 
                mr: 1,
                maxWidth: 150
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {userName}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {email}
                </Typography>
              </Box>

              {/* User Profile Menu */}
              <Tooltip title="Account menu">
                <IconButton 
                  color="inherit" 
                  onClick={handleProfileMenuOpen}
                  sx={iconButtonStyle}
                >
                  <Avatar 
                    sx={{ 
                      width: 36, 
                      height: 36,
                      backgroundColor: alpha(theme.palette.background.paper, 0.2),
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      fontSize: '1rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {getUserInitials()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Toolbar>

        {/* Search Menu */}
        <Menu
          anchorEl={searchAnchorEl}
          open={Boolean(searchAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              width: 400,
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }
          }}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <InputBase
              inputRef={searchInputRef}
              fullWidth
              placeholder="Search guests, rooms, bookings, services..."
              value={searchQuery}
              onChange={handleSearch}
              startAdornment={<Search sx={{ mr: 1, color: 'text.secondary' }} />}
              sx={{ fontSize: '0.9rem' }}
            />
          </Box>
        </Menu>

       
          

        {/* Profile Dropdown Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              width: 280,
              borderRadius: 3,
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
              overflow: 'hidden'
            }
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* Profile Header */}
          <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'white' }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap>
              {email}
            </Typography>
            <Chip
              label={isAdmin ? 'Hotel Manager' : 'Hotel Staff'}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontSize: '0.6rem',
                height: '20px'
              }}
            />
          </Box>

          <MenuItem onClick={handleOpenPasswordDialog}>
            <ListItemIcon>
              <Lock fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </MenuItem>

          {isAdmin && (
            <MenuItem onClick={() => handleNavigate('/hotel/admin')}>
              <ListItemIcon>
                <AdminPanelSettings fontSize="small" />
              </ListItemIcon>
              <ListItemText>Admin Panel</ListItemText>
            </MenuItem>
          )}

          <Divider />

          <MenuItem 
            onClick={handleLogout}
            sx={{ 
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.04)'
              }
            }}
          >
            <ListItemIcon>
              <ExitToApp fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </AppBar>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" fontWeight="bold">
            Change Password
          </Typography>
          <IconButton 
            onClick={handleClosePasswordDialog} 
            sx={{ color: 'white' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {passwordMessage.text && (
            <Alert 
              severity={passwordMessage.type} 
              sx={{ mb: 3, mt: 2 }}
              onClose={() => setPasswordMessage({ type: '', text: '' })}
            >
              {passwordMessage.text}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              label="Current Password"
              name="currentPassword"
              type="password"
              value={passwordFormData.currentPassword}
              onChange={handlePasswordInputChange}
              margin="normal"
              required
              disabled={passwordLoading}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="New Password"
              name="newPassword"
              type="password"
              value={passwordFormData.newPassword}
              onChange={handlePasswordInputChange}
              margin="normal"
              required
              disabled={passwordLoading}
              helperText="Password must be at least 6 characters long"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Confirm New Password"
              name="confirmPassword"
              type="password"
              value={passwordFormData.confirmPassword}
              onChange={handlePasswordInputChange}
              margin="normal"
              required
              disabled={passwordLoading}
              sx={{ mb: 3 }}
            />

            <Box sx={{ 
              p: 2, 
              backgroundColor: alpha(theme.palette.info.main, 0.1), 
              borderRadius: 2,
              mb: 2
            }}>
              <Typography variant="body2" color="info.main" gutterBottom>
                <strong>Password Requirements:</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                • At least 6 characters long
                <br />
                • Different from current password
                <br />
                • Confirmed correctly in both fields
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={handleClosePasswordDialog}
            disabled={passwordLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordSubmit}
            disabled={passwordLoading}
            variant="contained"
            sx={{ minWidth: 120 }}
          >
            {passwordLoading ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HotelTopNav;